import { Customer } from "@/hooks/auth";
import { CustomerDetails, ICoupon } from "@/types/data";

interface CustomerDataDto {
  barcode: string;
  gender: string;
  godina_rodenja: string;
  neighbourhood: string;
  fish_bodovi: number;
  totalspend: string;
  numberoftransactions: string;
  numberofusedcoupons: string;
  numberofboughtcoupons: string;
  username: string;
  nick: string;
  first_name?: string | null;
  surname?: string | null;
  date_of_birth?: string | null;
}

export const convertCustomersResponse = (
  customerData: CustomerDataDto[]
): Customer[] => {
  return customerData.map(
    (c) =>
      ({
        ...c,
        gender: c.gender as "ž" | "m" | undefined,
        spol: c.gender === "ž" ? "ž" : "m",
        kvart: c.neighbourhood,
        barkod: c.barcode,
        godina_rodenja: parseInt(c.godina_rodenja, 10),
        totalSpend: c.totalspend,
        totalPoints: c.fish_bodovi,
        numberOfTransactions: parseInt(c.numberoftransactions, 10),
        numberOfUsedCoupons: parseInt(c.numberofusedcoupons, 10),
        numberOfBoughtCoupons: parseInt(c.numberofboughtcoupons, 10),
        email: c.username,
        username: c.nick,
        first_name: c.first_name ?? null,
        surname: c.surname ?? null,
        date_of_birth: c.date_of_birth ?? null,
      } satisfies Customer)
  );
};

export interface CouponDto {
  nft_id: number;
  dostupan: boolean;
  act_1_popust: boolean | null;
  popust_1_popust: number | null;
  act_izbor: boolean | null;
  grupa_izbor: number | null;
  opis_izbor: string | null;
  popust_izbor: number | null;
  act_myst: boolean | null;
  broj_myst: number | null;
  art1_grp: number | null;
  art1_opis: string | null;
  art1_popust: number | null;
  art2_grp: number | null;
  art2_opis: string | null;
  art2_popust: number | null;
  art3_grp: number | null;
  art3_opis: string | null;
  art3_popust: number | null;
  art4_grp: number | null;
  art4_opis: string | null;
  art4_popust: number | null;
  art5_grp: number | null;
  art5_opis: string | null;
  art5_popust: number | null;
  sifra_1_popust: string | null;
  art1_sifra: string | null;
  art2_sifra: string | null;
  art3_sifra: string | null;
  art4_sifra: string | null;
  art5_sifra: string | null;
  naziv: string;
  cijena: number;
  kategorija: string;
  kategorija_id: number;
  id: number;
}

export const convertCouponDtoToFormData = (dto: CouponDto): ICoupon => {
  const {
    id,
    nft_id,
    naziv,
    cijena,
    kategorija,
    dostupan,
    sifra_1_popust,
    popust_1_popust,
    grupa_izbor,
    opis_izbor,
    popust_izbor,
    act_1_popust,
    act_izbor,
    act_myst,
    art1_sifra,
    art2_sifra,
    art3_sifra,
    art4_sifra,
    art5_sifra,
    art1_popust,
    art2_popust,
    art3_popust,
    art4_popust,
    art5_popust,
    art1_grp,
    art2_grp,
    art3_grp,
    art4_grp,
    art5_grp,
    art1_opis,
    art2_opis,
    art3_opis,
    art4_opis,
    art5_opis,
  } = dto;

  const determineVariant = (): "act_1_popust" | "act_izbor" | "act_myst" => {
    if (act_1_popust) return "act_1_popust";
    if (act_izbor) return "act_izbor";
    if (act_myst) return "act_myst";
    return "act_1_popust"; // fallback default
  };

  const art_fields = [
    {
      sifra: art1_sifra ?? "",
      popust: art1_popust ?? undefined,
      grp: art1_grp ?? undefined,
      opis: art1_opis ?? "",
    },
    {
      sifra: art2_sifra ?? "",
      popust: art2_popust ?? undefined,
      grp: art2_grp ?? undefined,
      opis: art2_opis ?? "",
    },
    {
      sifra: art3_sifra ?? "",
      popust: art3_popust ?? undefined,
      grp: art3_grp ?? undefined,
      opis: art3_opis ?? "",
    },
    {
      sifra: art4_sifra ?? "",
      popust: art4_popust ?? undefined,
      grp: art4_grp ?? undefined,
      opis: art4_opis ?? "",
    },
    {
      sifra: art5_sifra ?? "",
      popust: art5_popust ?? undefined,
      grp: art5_grp ?? undefined,
      opis: art5_opis ?? "",
    },
  ];

  return {
    id,
    nft_id,
    naziv,
    cijena,
    kategorija,
    dostupan,
    variant: determineVariant(),
    popust_1_popust: popust_1_popust ?? undefined,
    sifra_1_popust: sifra_1_popust ?? "",
    grupa_izbor: grupa_izbor ?? undefined,
    opis_izbor: opis_izbor ?? "",
    popust_izbor: popust_izbor ?? undefined,
    art_fields,
  };
};

export const convertToServerFormat = (coupon: ICoupon): CouponDto => {
  const artFields = coupon.art_fields || [];

  const couponIdMap = coupon.id ? { id: coupon.id } : {};

  // @ts-expect-error id not present if not in edit mode
  return {
    nft_id: coupon.nft_id,
    dostupan: coupon.dostupan,
    act_1_popust: coupon.variant === "act_1_popust" ? true : null,
    popust_1_popust: coupon.popust_1_popust || null,
    act_izbor: coupon.variant === "act_izbor" ? true : null,
    grupa_izbor: coupon.grupa_izbor || null,
    opis_izbor: coupon.opis_izbor || null,
    popust_izbor: coupon.popust_izbor || null,
    act_myst: coupon.variant === "act_myst" ? true : null,
    broj_myst: null, // You can assign actual data here if needed
    art1_grp: artFields[0]?.grp || null,
    art1_opis: artFields[0]?.opis || null,
    art1_popust: artFields[0]?.popust || null,
    art2_grp: artFields[1]?.grp || null,
    art2_opis: artFields[1]?.opis || null,
    art2_popust: artFields[1]?.popust || null,
    art3_grp: artFields[2]?.grp || null,
    art3_opis: artFields[2]?.opis || null,
    art3_popust: artFields[2]?.popust || null,
    art4_grp: artFields[3]?.grp || null,
    art4_opis: artFields[3]?.opis || null,
    art4_popust: artFields[3]?.popust || null,
    art5_grp: artFields[4]?.grp || null,
    art5_opis: artFields[4]?.opis || null,
    art5_popust: artFields[4]?.popust || null,
    sifra_1_popust: coupon.sifra_1_popust || null,
    art1_sifra: artFields[0]?.sifra || null,
    art2_sifra: artFields[1]?.sifra || null,
    art3_sifra: artFields[2]?.sifra || null,
    art4_sifra: artFields[3]?.sifra || null,
    art5_sifra: artFields[4]?.sifra || null,
    naziv: coupon.naziv,
    cijena: coupon.cijena,
    kategorija: coupon.kategorija,
    kategorija_id: coupon.kategorija_id || 0,
    ...couponIdMap,
  };
};

export interface CustomerDetailsDto {
  username: string;
  nick: string;
  barcode: string;
  gender: string;
  godina_rodenja: string;
  neighbourhood: string;
  fish_bodovi: number;
  first_name?: string | null;
  surname?: string | null;
  date_of_birth?: string | null;
  fcm_token?: string | null;
  total_spend: string;
  numberOfTransactions: string;
  transactionsPerStore: {
    name: string;
    value: number;
  }[];
  topProducts: {
    name: string;
  }[];
  lastInvoices: {
    ime_ducana: string;
    datum_i_vrijeme: string;
    mp_racun_broj: string;
    ukupni_iznos: string;
    bodovi: number;
  }[];
  coupons: {
    count: string;
    name: string;
    status: string;
  }[];
  numberofUsedCoupons: {
    count: string;
  };
  numberofBoughtCoupons: {
    count: string;
  };
}

const tryParseInt = (value: string | number) => {
  if (typeof value === "number") return value;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
};

export const convertCustomerStatsResponse = (
  response: CustomerDetailsDto
): CustomerDetails => {
  const returnValue = {
    ...response,
    transactionsPerStore: response.transactionsPerStore.map((transcation) => ({
      ...transcation,
      value: tryParseInt(transcation.value),
    })),

    coupons: response.coupons.reduce((acc, iter) => {
      const item = {
        title: iter.name,
        subtitle: iter.status,
        status: iter.status === "iskoristen" ? "Expired" : "Active",
      };

      const itemCount = tryParseInt(iter.count || 1);

      const multiplyResponseByCount = Array.from(
        { length: itemCount },
        (_, _i) => {
          return item;
        }
      );

      return [...acc, ...multiplyResponseByCount];
    }, [] as CustomerDetails["coupons"]),
  };

  return returnValue;
};
